import { GlobalSpin } from "#src/components/global-spin";
import { Scrollbar } from "#src/components/scrollbar";
import { useLayoutContentStyle } from "#src/hooks/use-layout-style";
import { CSS_VARIABLE_LAYOUT_CONTENT_HEIGHT, ELEMENT_ID_MAIN_CONTENT } from "#src/layout/constants";
import { useAccessStore } from "#src/store/access";
import { usePreferencesStore } from "#src/store/preferences";
import { useTabsStore } from "#src/store/tabs";
import { theme } from "antd";
import KeepAlive, { useKeepAliveRef } from "keepalive-for-react";
import { useEffect, useMemo } from "react";
import { useLocation, useOutlet } from "react-router";

export interface LayoutContentProps {
	computedSidebarWidth: number
}

export default function LayoutContent({ computedSidebarWidth }: LayoutContentProps) {
	const {
		token: { colorBgLayout },
	} = theme.useToken();
	const { pathname, search } = useLocation();
	const outlet = useOutlet();
	const { contentElement } = useLayoutContentStyle();

	const aliveRef = useKeepAliveRef();
	const isRefresh = useTabsStore(state => state.isRefresh);
	const openTabs = useTabsStore(state => state.openTabs);
	const tabbarEnable = usePreferencesStore(state => state.tabbarEnable);
	const flatRouteList = useAccessStore(state => state.flatRouteList);
	const transitionName = usePreferencesStore(state => state.transitionName);
	const transitionEnable = usePreferencesStore(state => state.transitionEnable);
	// const enableFooter = usePreferencesStore(state => state.enableFooter);
	// const fixedFooter = usePreferencesStore(state => state.fixedFooter);

	/**
	 * To distinguish different pages for caching
	 */
	const cacheKey = useMemo(() => {
		return pathname + search;
	}, [pathname, search]);

	/**
	 * When functions like closing the current tab, closing right/left/other tabs,
	 * or closing all tabs are used, the cache for the closed tab needs to be destroyed.
	 */
	useEffect(() => {
		const cacheNodes = aliveRef.current?.getCacheNodes?.();
		cacheNodes?.forEach((node) => {
			if (!openTabs.has(node.cacheKey)) {
				aliveRef.current?.destroy(node.cacheKey);
			}
		});
	}, [openTabs]);

	/**
	 * If the multi-tab function is disabled, clear all cached pages.
	 */
	useEffect(() => {
		if (!tabbarEnable) {
			const cacheNodes = aliveRef.current?.getCacheNodes?.();
			cacheNodes?.forEach((node) => {
				/* Not including the current page */
				if (node.cacheKey !== cacheKey) {
					aliveRef.current?.destroy(node.cacheKey);
				}
			});
		}
	}, [tabbarEnable]);

	/* KeepAlive refresh (tab bar button + header vessel switch) */
	useEffect(() => {
		if (isRefresh) {
			aliveRef.current?.refresh();
		}
	}, [isRefresh]);

	/* Routes with keepAlive = false are not cached */
	const keepAliveExclude = useMemo(() => {
		/**
		 * If the multi-tab function is not enabled, KeepAlive is not needed.
		 * To preserve page transition animations, all routes are added to the exclude array.
		 */
		if (!tabbarEnable) {
			return Object.keys(flatRouteList);
		}
		return Object.entries(flatRouteList).reduce<string[]>((acc, [key, value]) => {
			if (value.handle.keepAlive === false) {
				acc.push(key);
			}
			return acc;
		}, []);
	}, [flatRouteList, tabbarEnable]);

	return (
		<main
			id={ELEMENT_ID_MAIN_CONTENT}
			ref={contentElement}
			className="relative overflow-y-auto overflow-x-hidden flex-grow"
			style={
				{
					paddingLeft: computedSidebarWidth,
					backgroundColor: colorBgLayout,
				}
			}
		>
			<Scrollbar>
				<GlobalSpin>
					<div
						className="flex flex-col h-full"
					>
						<div
							style={{
								height: `var(${CSS_VARIABLE_LAYOUT_CONTENT_HEIGHT})`,
							}}
						>
							<KeepAlive
								max={20}
								transition
								duration={300}
								cacheNodeClassName={transitionEnable ? `keepalive-${transitionName}` : undefined}
								exclude={keepAliveExclude}
								activeCacheKey={cacheKey}
								aliveRef={aliveRef}
							>
								{outlet}
							</KeepAlive>
						</div>
						{/* {enableFooter && !fixedFooter ? <LayoutFooter /> : null} */}
					</div>
				</GlobalSpin>
			</Scrollbar>

		</main>
	);
}
